import { extendZodWithOpenApi } from '@anatine/zod-openapi'
import z from 'zod'
import { FeedingPlanType } from '../common'
import { PlanCategory } from './plan.types'
import { Meal } from './types'

extendZodWithOpenApi(z)

export const PlanBaseSchema = z.object({
  createdAt: z.date({ coerce: true }),
  updatedAt: z.date({ coerce: true }),
  deletedAt: z.date({ coerce: true }).optional().nullable(),
  version: z.number().int().gt(0),
})

export const PreferredMealSchema = z.object({
  type: z.nativeEnum(Meal),
  weight: z.number().int().gt(0),
})

export type PreferredMeal = z.infer<typeof PreferredMealSchema>

export const MealQuantitySchema = z.object({
  type: z.nativeEnum(Meal),
  sku: z.string(),
  quantity: z.number().int().gt(0),
})

export type MealQuantity = z.infer<typeof MealQuantitySchema>

export const LykaServingSizeSchema = z.object({
  id: z.string(),
  slug: z.string(),
  servingSize: z.number().int().gt(0),
  pouchSize: z.number().int().gt(0),
})

export type LykaServingSize = z.infer<typeof LykaServingSizeSchema>

export const LykaPuppyServingSizeDogInputSchema = z.object({
  adultMealWeightV1: z.number().nonnegative(),
  mealWeightV1: z.number().nonnegative(),
  mealWeightV2: z.number().nonnegative(),
  breedAdultMealWeight: z.number().nonnegative(),
})
export type LykaPuppyServingSizeDogInput = z.infer<typeof LykaPuppyServingSizeDogInputSchema>

export const LykaGeneratePuppyServingSizesBodySchema = z.object({
  dogs: LykaPuppyServingSizeDogInputSchema.array().min(1),
})
export type LykaGeneratePuppyServingSizesBody = z.infer<typeof LykaGeneratePuppyServingSizesBodySchema>

export const LykaPuppyServingSizeItemSchema = z.object({
  servingSize: z.number().int().gt(0),
  adultServingSize: z.number().int().gt(0),
})
export type LykaPuppyServingSizeItem = z.infer<typeof LykaPuppyServingSizeItemSchema>

export const LykaGeneratePuppyServingSizesResponseSchema = z.object({
  servingSizes: LykaPuppyServingSizeItemSchema.array(),
})
export type LykaGeneratePuppyServingSizesResponse = z.infer<typeof LykaGeneratePuppyServingSizesResponseSchema>

export const LykaGeneratePlansBodyInputSchema = z.object({
  animals: z.object({
    animalId: z.string().uuid(),
    animalDailyEnergyKilocalories: z.number().nonnegative(),
    puppyMealWeights: LykaPuppyServingSizeDogInputSchema.nullish(),
    overrideServingSizeInGrams: z.number().int().gt(0).optional(),
    overrideServingSizeForCadence: z.number().int().gt(0).nullish(),
    preferredMeals: PreferredMealSchema.array().min(1).optional(),
  }).array().min(1),
  customCadenceInWeeks: z.number().int().gt(0).nullish(),
  customBoxInWeeks: z.number().int().gt(0).nullish(),
  /**
   * When true, 700g is served as 2 x 350g pouches (d0700) and 1400g as 4 x 350g pouches (q1400)
   * instead of the default 1 x 700g (f0700) and 2 x 700g (d1400). Defaults to false when omitted.
   */
  enable700gDoublePouches: z.boolean().optional(),
  /**
   * When true, a household meeting Topper Eligibility also gets Topper and Topper Starter Box
   * rows. Off by default so callers that price or list every row never see them unasked.
   */
  includeTopperPlans: z.boolean().optional(),
  /**
   * When true, every animal in this call gets a Topper row and a Topper starter-box row.
   * Signup will not build those rows for a puppy, a second dog, or a dog outside 500–800g,
   * and a later lookup of the household's current plan then throws.
   * Optional like includeTopperPlans: the generator treats a missing flag as off.
   */
  forceTopperPlans: z.boolean().optional(),
})
export type LykaGeneratePlansBodyInput = z.infer<typeof LykaGeneratePlansBodyInputSchema>

export const LykaGeneratePlansBodySchema = z.object({
  animalId: z.string().uuid(),
  animalDailyEnergyKilocalories: z.number().gt(0),
  overrideServingSizeInGrams: z.number().int().gt(0).optional(),
  preferredMeals: PreferredMealSchema.array().min(1).optional(),
}).array().min(1)

/** @deprecated Use `GenerateLykaPlansBody` */
export type GeneratePlansBody = z.infer<typeof LykaGeneratePlansBodySchema>
export type LykaGeneratePlansBody = GeneratePlansBody

export const LykaGeneratedPlanSchema = z.object({
  planId: z.string().uuid(),
  planSlug: z.nativeEnum(FeedingPlanType),
  animalId: z.string().uuid(),
  servingSize: z.number().int().gt(0),
  cadence: z.number().int().gt(0),
  mealQuantities: MealQuantitySchema.array(),
  pouchSize: z.number().int().gt(0),
  totalPouches: z.number().int().gt(0),
})

/** @deprecated Use `LykaGeneratedPlan` */
export type GeneratedPlan = z.infer<typeof LykaGeneratedPlanSchema>
export type LykaGeneratedPlan = GeneratedPlan

export const LykaGeneratePlanOutputSchema = z.object({
  planId: z.string().uuid(),
  planSlug: z.nativeEnum(FeedingPlanType),
  animalId: z.string().uuid(),
  /** The Full-Bowl Serving in grams, except on Topper rows, where it is the fixed 125g shipped a day. */
  servingSize: z.number().int().gt(0),
  /** Set on Topper rows only: the dog's Full-Bowl Serving, for the share of daily needs one pouch covers. */
  fullBowlServingSize: z.number().int().gt(0).optional(),
  cadence: z.number().int().gt(0),
  standardCadence: z.number().int().gt(0),
  mealQuantities: MealQuantitySchema.array(),
  pouchSize: z.number().int().gt(0),
  totalPouches: z.number().int().gt(0),
  standardTotalPouches: z.number().int().gt(0),
  // Populated only when `puppyMealWeights` is provided on the request; otherwise null.
  adultServingSize: z.number().int().gt(0).nullable(),
  isPuppy: z.boolean(),
})
export type LykaGeneratePlanOutput = z.infer<typeof LykaGeneratePlanOutputSchema>

export const TygaMeals = {
  Pork: 'tyga_pork',
  Chicken: 'tyga_chicken',
  Turkey: 'tyga_turkey',
  Kangaroo: 'tyga_kangaroo',
} as const

export type TygaMeal = typeof TygaMeals[keyof typeof TygaMeals]

// The number of SKUs to attempt to allocate to a box. We offer volume discounts for orders with more SKUs:
// https://www.notion.so/lykapetfood/Tyga-Pricing-model-PBC-29c78de5664280c8bf03db5d33d2e032
// At time of writing this is:
// - 5% for 20+ SKUs
// - 12% for 23+ SKUs
export const TygaBoxStrategy = {
  Small: 'small', // <20 skus
  Medium: 'medium', // 20+ skus
  Large: 'large', // 23+ skus
} as const

export type TygaBoxStrategy = typeof TygaBoxStrategy[keyof typeof TygaBoxStrategy]

export const TygaGeneratePlansBodySchema = z.object({
  boxStrategy: z.enum([
    TygaBoxStrategy.Small,
    TygaBoxStrategy.Medium,
    TygaBoxStrategy.Large,
  ]).optional(),
  // Optionally override the default cadence (in days) for the generated plans
  cadence: z.number().int().gte(0).optional(),
  // Optionally override the box limit. Defaults to 1 box which can (at time of writing) contain up to 36 SKUs
  // Currently limited to 2 boxes as we have an upper-limit of 53 SKUs in a single order
  boxLimit: z.number().int().gt(0).lte(2).optional(),
  // The list of product SKUs to consider when generating the plans.
  // This feels a bit clunky as the generator needs to understand which SKUs correspond to which meals.
  // If we ever end up merging the product and subscription services this can likely be improved.
  meals: z.enum([
    TygaMeals.Pork,
    TygaMeals.Chicken,
    TygaMeals.Turkey,
    TygaMeals.Kangaroo,
  ]).array().min(1),

  animals: z.object({
    id: z.string().uuid(),
    /** Optionally override the number of SKUs to feed per day */
    skusPerDay: z.number().positive().optional(),
    dailyEnergyKilocalories: z.number().gte(0),
    minDailyEnergyKilocalories: z.number().gte(0),

  }).array(),

  /**
   * The consumer can provide custom meal allocation ratios rather than using the default logic:
   * @example
   * {
   *   'tyga-au-meal-pork-0170': 3,
   *   'tyga-au-meal-turkey-0170': 1,
   *   'tyga-au-meal-chicken-0170': 1,
   * }
   */
  mealAllocationRatios: z.record(z.string(), z.number()).optional(),
})

export type TygaGeneratePlansBody = z.infer<typeof TygaGeneratePlansBodySchema>

export const TygaGeneratedPlanSchema = z.object({
  /** Whether the given plan is enabled. Will be false if the given plan has too many or too few SKUs to ship */
  enabled: z.boolean(),
  planId: z.string(),
  planSlug: z.nativeEnum(FeedingPlanType),
  cadence: z.number().int().nonnegative(),
  skuSize: z.number().int().positive(),
  totalSkus: z.number().int(),
  // The list of meal SKU quantities for the plan. SKUs is better terminology here as Tyga SKUs are sold as twin-pouches
  // which can make 'pouches' a very confusing term.
  skus: z.object({
    sku: z.string(),
    quantity: z.number().int().nonnegative(),
  }).array(),
  animals: z.array(z.object({
    id: z.string().uuid(),
    caloriesFedPerDay: z.number().int().positive(),
    percentOfDailyCalories: z.number().positive(),
    /** The SKUs per day for the animal */
    skusPerDay: z.number().positive(),
    /** The recommended number of SKUs per day ignoring any `skusPerDay` override that may have been provided as input */
    recommendedSkusPerDay: z.number().positive(),
  })),
})

export type TygaGeneratedPlan = z.infer<typeof TygaGeneratedPlanSchema>

export const RecommendationPlanSchema = z.object({
  planId: z.string().uuid(),
  planSlug: z.nativeEnum(FeedingPlanType),
  cadence: z.number().int().gt(0),
  animalPlans: LykaGeneratedPlanSchema.array(),
})

export type RecommendationPlan = z.infer<typeof RecommendationPlanSchema>

export const PlanSchema = PlanBaseSchema.extend({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.nativeEnum(FeedingPlanType),
  category: z.nativeEnum(PlanCategory),
  percentOfCalories: z.number().gt(0).nullable(),
})

export type Plan = z.infer<typeof PlanSchema>

/**
 * The meal weight used to recommend a full bowl.
 * An adult uses dailyEnergyKilocalories. A puppy uses puppyMealWeights.
 */
export const GetRecommendedServingSizeBodySchema = z.object({
  dailyEnergyKilocalories: z.number().positive().nullable(),
  puppyMealWeights: z.object({
    adultMealWeightV1: z.number().nonnegative(),
    mealWeightV1: z.number().nonnegative(),
    mealWeightV2: z.number().nonnegative(),
    breedAdultMealWeight: z.number().nonnegative(),
  }).nullish(),
})

export type GetRecommendedServingSizeBody = z.infer<typeof GetRecommendedServingSizeBodySchema>

/**
 * The full bowl Lyka recommends from that meal weight, in grams.
 * The serving size the customer picked is not used.
 */
export const RecommendedServingSizeSchema = z.object({
  recommendedServingSizeInGrams: z.number().int().positive(),
})

export type RecommendedServingSize = z.infer<typeof RecommendedServingSizeSchema>

export const UUIDOrSlugSchema = z.string().refine(
  (val) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    return uuidRegex.test(val) || Object.values(FeedingPlanType).includes(val as FeedingPlanType)
  },
  {
    message: 'Identifier must be a valid UUID or a valid slug',
  },
)
